

import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AboutusImage from "../../Logos/Aboutus.png";
import screenimage from "../../Logos/screenimage.png";
import Footer from "../../Common/Footer";

const AboutUs = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));     // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // < 900px

  const points = [
    "Quality healthcare and emotional support delivered to your home",
    "Routine checkups and care for elderly family members",
    "Safe medicine delivery directly to your doorstep",
    "Escort services for children to diagnostic labs",
    "Swift and reliable response during medical emergencies",
    "Trusted, timely, and compassionate care—whenever it's needed",
  ];

  const services = [
    "Medical and diagnostic home delivery",
    "Emergency support with 24/7 readiness",
    "Transportation for hospital visits",
    "Routine health checkups and caregiver assistance",
    "Child care, elderly companionship, and social wellness activities",
  ];

  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh", pt: 0, pb: 0, m: 0 }}>

      {/* ── Hero / Mission & Vision ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          alignItems: "stretch",
          minHeight: isSmallScreen ? "auto" : "5vh",
          gap: 0,
        }}
      >
        {/* Left – Image */}
        <Box sx={{ flex: 1, maxHeight: isSmallScreen ? "260px" : "none" }}>
          <Box
            component="img"
            src={AboutusImage}
            alt="About Us"
            sx={{
              width: isSmallScreen ? "100%" : "90%",
              height: "100%",
              objectFit: "cover",
              borderRadius: isSmallScreen ? "0 0 60px 0" : "0 0 100px 0",
              display: "block",
            }}
          />
        </Box>

        {/* Right – Mission + Vision cards */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: isSmallScreen ? "16px" : "0px",
            gap: isSmallScreen ? "16px" : "0px",
          }}
        >
          {/* Mission */}
          <Box
            sx={{
              background: "#d4f8d4",
              padding: isMobile ? "16px" : "20px",
              marginBottom: isSmallScreen ? 0 : "20px",
              borderRadius: isSmallScreen ? "0 0 0 60px" : "0 0 0 100px",
              width: isSmallScreen ? "100%" : "60%",
              minHeight: isSmallScreen ? "auto" : "150px",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                ml: isMobile ? 1 : 3,
                fontSize: isMobile ? "22px" : "30px",
                fontWeight: "bold",
                mb: 1,
              }}
            >
              Our Mission
            </Typography>
            <Typography
              sx={{ ml: isMobile ? 1 : 3, fontSize: isMobile ? "14px" : "20px" }}
            >
              To provide comprehensive medical, physical, mental, and emotional care
              directly to your doorstep using intelligent technology and compassionate
              human touch, making healthcare accessible for everyone.
            </Typography>
          </Box>

          {/* Vision */}
          <Box
            sx={{
              background: "#052c54",
              padding: isMobile ? "16px" : "20px",
              color: "white",
              borderRadius: isSmallScreen ? "0 0 0 60px" : "0 0 0 100px",
              width: isSmallScreen ? "100%" : "60%",
              minHeight: isSmallScreen ? "auto" : "150px",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                ml: isMobile ? 1 : 3,
                fontSize: isMobile ? "22px" : "30px",
                fontWeight: "bold",
                mb: 1,
              }}
            >
              Our Vision
            </Typography>
            <Typography
              sx={{ ml: isMobile ? 1 : 3, fontSize: isMobile ? "14px" : "20px" }}
            >
              Unique care — no family member is forgotten. We are by your side.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Screen divider image */}
      <Box
        component="img"
        src={screenimage}
        alt="About Us"
        sx={{
          width: "100%",
          height: isMobile ? "6vh" : "10vh",
          objectFit: "cover",
          borderRadius: "0px 0px 100px 0px",
          display: "block",
        }}
      />

      {/* ── About Ayuja paragraph ── */}
      <Box
        sx={{
          bgcolor: "#006D77",
          color: "white",
          borderRadius: isMobile ? "40px 0px 40px 0px" : "100px 0px 100px 0px",
          p: isMobile ? 3 : isTablet ? 4 : 6,
          mb: 4,
          mx: isMobile ? 0 : 0,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: isMobile ? 14 : isTablet ? 16 : 20,
            fontFamily: "Montserrat",
            ml: isMobile ? 1 : isTablet ? 4 : 40,
            mr: isMobile ? 1 : isTablet ? 4 : 40,
            lineHeight: 1.7,
            textAlign: isMobile ? "justify" : "left",
          }}
        >
          <Box component="span" sx={{ fontSize: isMobile ? "1.6rem" : "2.5rem", fontWeight: "bold" }}>
            Ayuja
          </Box>{" "}
          Life Care is a tech-enabled community healthcare platform delivering
          comprehensive medical services to gated community residents. Our services
          include medicine delivery, diagnostic sample collection, elderly care,
          childcare, women and maternity care, emergency support, nursing services,
          and wellness programs. Our focus is on creating a seamless, timely, and
          personalized healthcare experience combining technology with human touch.
        </Typography>
      </Box>

      {/* ── Compassionate Care Section ── */}
      <Box
        sx={{
          border: "1.5px solid #006D77",
          borderRadius: "10px",
          p: isMobile ? 2 : 3,
          maxWidth: isMobile ? "95%" : "80%",
          mx: "auto",
          mt: 5,
          position: "relative",
          boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
        }}
      >
        {/* Header Tab */}
        <Box
          sx={{
            position: "absolute",
            top: "-20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#E6F4F3",
            border: "2px solid #006D77",
            borderRadius: "50px",
            px: 3,
            py: 0.5,
            whiteSpace: isMobile ? "normal" : "nowrap",
            textAlign: "center",
            width: isMobile ? "80%" : "auto",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "#006D77", textAlign: "center", fontSize: isMobile ? "12px" : "16px" }}
          >
            Compassionate Healthcare at Your Doorstep
          </Typography>
        </Box>

        {/* Points */}
        <Box sx={{ mt: 4 }}>
          {points.map((point, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                ml: isMobile ? 0 : isTablet ? 2 : "50px",
                mb: 2.5,
              }}
            >
              <CheckCircleIcon sx={{ color: "#00C853", mr: 1, mt: "2px", flexShrink: 0 }} />
              <Typography variant="body1" sx={{ fontSize: isMobile ? "14px" : "16px" }}>
                {point}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Services Section ── */}
      <Box
        sx={{
          textAlign: "center",
          py: isMobile ? 4 : 6,
          px: isMobile ? 2 : 2,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#22577A",
            fontSize: isMobile ? 16 : isTablet ? 20 : 25,
            fontWeight: 500,
            maxWidth: "700px",
            textAlign: "center",
            mx: "auto",
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          By combining the power of personalized human care{!isMobile && <br />}
          with intelligent technology, Ayuja offers{!isMobile && <br />}
          a wide range of services including:
        </Typography>

        <Grid
          container
          spacing={3}
          justifyContent="center"
          sx={{ maxWidth: "900px", mx: "auto", mb: 4 }}
        >
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: isMobile ? "flex-start" : "center",
                  textAlign: "left",
                  px: isMobile ? 1 : 0,
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: "#fff",
                    backgroundColor: "#006D77",
                    borderRadius: "20px",
                    mr: 1,
                    mt: "3px",
                    padding: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: "#006D77", fontWeight: "bold", fontSize: isMobile ? "14px" : "16px" }}
                >
                  {service}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Button */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => navigate("/serviceweprovide")}
            sx={{
              backgroundColor: "#006D77",
              px: 4,
              py: 1,
              borderRadius: "25px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: isMobile ? "14px" : "16px",
              "&:hover": { backgroundColor: "#00695c" },
            }}
          >
            View All Services
          </Button>
        </Box>
      </Box>

      {/* ── Bottom Banner ── */}
      <Box
        sx={{
          bgcolor: "#22577A",
          color: "white",
          textAlign: "center",
          borderRadius: 2,
          p: isMobile ? 2 : 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#fff",
            fontWeight: 500,
            maxWidth: "900px",
            textAlign: "center",
            mx: "auto",
            mb: 1,
            lineHeight: 1.6,
            fontSize: isMobile ? "14px" : isTablet ? "16px" : "20px",
          }}
        >
          Our platform connects residents, caregivers, healthcare professionals,
          and operations staff through an intuitive app ecosystem—making bookings,
          rescheduling, feedback, and care coordination seamless and stress-free.
        </Typography>
      </Box>

      <Footer />
    </Box>
  );
};

export default AboutUs;